import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, withLatestFrom, take } from 'rxjs/operators';
import { ApiUserService } from '@core/api/services/user/api-user.service';
import { AuthUserMapper } from '@core/domain/mappers/user/auth-user.mapper';
import { userActions } from '@store/actions/user.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, EMPTY, of, tap } from 'rxjs';
import { errorFromHttpErrorResponse } from '@core/services/request/util/error-from-http';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { userSelectors } from '@store/selectors/user.selectors';
import { MyAccountMapper } from '@core/domain/mappers/user/my-account.mapper';
import { LoadingIds } from '@shared/constants/loading';
import { MyAccountEditFormModel } from '@pages/my-account/my-account-edit/model/my-account-edit-form.model';
import { AuthUser } from '@core/domain/model/user/auth-user.model';
import { FloatingAlertsService } from '@shared/components/floating-alerts/data/floating-alerts.service';
import { AcRoutingService } from '@core/services/routing/ac-routing.service';
import { STORAGE_KEYS, StorageService } from '@core/services/storage/storage.service';
import { authActions } from '@store/actions/auth.actions';
import { MatDialog } from '@angular/material/dialog';
import { mapArray } from 'ngxtension/map-array';
import { CountrySelectionDialog } from '@shared/components/country-selection/country-selection.dialog';
import { EnergyRehabilitationSelectionDialog } from '@shared/components/energy-rehabilitation-selection/energy-rehabilitation-selection.dialog';
import { filterNil } from 'ngxtension/filter-nil';
import { DataProtectionDialog } from '@shared/components/data-protection-dialog/data-protection.dialog';

const HOME_ROUTE = '/home';
const REHAB_ROUTE = '/consumption-invoices';

@Injectable()
export class UserEffects {
  fetchUser$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(userActions.fetchUser),
      withLatestFrom(this._store.select(userSelectors.country)),
      switchMap(([, currentCountry]) =>
        this._apiUser.fetchUser().pipe(
          map((response) => response.data),
          mapArray(AuthUserMapper.userFromApi),

          switchMap((users) => {
            if (users.length === 1) {
              return this._resolveSingleUserFetch(users[0]);
            }

            if (currentCountry) {
              return of(
                userActions.setCountry({
                  user: users.filter((user) => user.country === currentCountry)[0],
                  multi: true,
                })
              );
            }

            return of(userActions.chooseCountry({ users }));
          }),

          catchError((err: HttpErrorResponse) => of(userActions.setError({ error: errorFromHttpErrorResponse(err) })))
        )
      )
    );
  });

  midFetchUser$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(userActions.midFetchUser),
      withLatestFrom(this._store.select(userSelectors.country)),
      switchMap(([, currentCountry]) => {
        return this._apiUser.fetchUser().pipe(
          map((response) => response.data),
          mapArray(AuthUserMapper.userFromApi),
          map((users) => {
            if (users.length === 1) {
              return userActions.updateUser({ user: users[0] });
            } else if (currentCountry) {
              return userActions.updateUser({ user: users.filter((value) => value.country === currentCountry)[0] });
            } else {
              return userActions.chooseCountry({ users });
            }
          }),
          catchError((err: HttpErrorResponse) => of(userActions.setError({ error: errorFromHttpErrorResponse(err) })))
        );
      })
    );
  });

  editUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.editUser),
      map((action): [MyAccountEditFormModel, AuthUser | null] => [action.editBody, this._store.selectSignal(userSelectors.authUser)()]),
      filter(([, user]) => user != null),
      switchMap(([editBody, user]) => {
        return this._apiUser.updateUser(MyAccountMapper.domainModelToApiBody(editBody, user!), LoadingIds.UPDATE_ACCOUNT).pipe(
          catchError(() => EMPTY),
          tap(() => {
            this._floatingAlerts.info('USER_MANAGEMENT.SAVED_USER_DATA');
            this._routing.goToMyAccount();
          }),
          map(() => editBody.email !== user?.email),
          map((logoff) => (logoff ? authActions.attemptLogout({}) : userActions.midFetchUser()))
        );
      })
    )
  );

  fetchImpersonation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.fetchImpersonation),
      switchMap(({ impersonationEmail }) => this._apiUser.impersonateUser(impersonationEmail).pipe(catchError(() => EMPTY))),
      map((response) => response.data.userToken),
      map((impersonationToken) => userActions.setImpersonation({ impersonationToken }))
    )
  );

  reloadOnSetImpersonation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(userActions.setImpersonation),
        tap(({ impersonationToken }) => {
          if (impersonationToken) {
            this._storageService.setItem(STORAGE_KEYS.IMPERSONATION_TOKEN, impersonationToken);
          } else {
            this._storageService.removeItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
          }
        }),
        debounceTime(500),
        tap(() => location.reload())
      ),
    {
      dispatch: false,
    }
  );

  chooseCountry$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.chooseCountry),
      switchMap(({ users }) =>
        this._dialog
          .open(CountrySelectionDialog, { disableClose: false, data: users })
          .afterClosed()
          .pipe(map((selectedUser) => ({ users, selectedUser })))
      ),
      filterNil(),
      switchMap(({ selectedUser }) => {
        // 🇵🇹 Portugal → siempre home
        if (selectedUser.country === 'PT') {
          return of(userActions.setCountry({ user: selectedUser, multi: true }), userActions.navigateAfterWebReady({ route: HOME_ROUTE }));
        }

        // 🇪🇸 España
        const comercializadora = selectedUser.informationCommercializer;
        const rehabilitacion = selectedUser.informationRehabilitation;

        if (comercializadora && rehabilitacion) {
          const storedMode = this._storageService.getItem(STORAGE_KEYS.ENERGY_REHABILITATION_MODE, 'session');

          if (storedMode != null) {
            const isEnabled = storedMode === 'true';
            return of(
              userActions.setCountry({ user: selectedUser, multi: true }),
              userActions.setEnergyRehabilitationMode({ enabled: isEnabled }),
              userActions.navigateAfterWebReady({ route: isEnabled ? REHAB_ROUTE : HOME_ROUTE })
            );
          }

          return this._dialog
            .open(EnergyRehabilitationSelectionDialog, { disableClose: false })
            .afterClosed()
            .pipe(
              filterNil(),
              switchMap((option) => {
                if (option === 'rehabilitation') {
                  return of(
                    userActions.setCountry({ user: selectedUser, multi: true }),
                    userActions.setEnergyRehabilitationMode({ enabled: true }),
                    userActions.navigateAfterWebReady({ route: REHAB_ROUTE })
                  );
                }

                return of(
                  userActions.setCountry({ user: selectedUser, multi: true }),
                  userActions.setEnergyRehabilitationMode({ enabled: false }),
                  userActions.navigateAfterWebReady({ route: HOME_ROUTE })
                );
              })
            );
        }

        if (rehabilitacion) {
          return of(
            userActions.setCountry({ user: selectedUser, multi: true }),
            userActions.setEnergyRehabilitationMode({ enabled: true }),
            userActions.navigateAfterWebReady({ route: REHAB_ROUTE })
          );
        }

        return of(
          userActions.setCountry({ user: selectedUser, multi: true }),
          userActions.setEnergyRehabilitationMode({ enabled: false }),
          userActions.navigateAfterWebReady({ route: HOME_ROUTE })
        );
      })
    )
  );

  setCountry$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.setCountry),
      tap(({ user, multi }) => {
        if (multi) {
          this._storageService.setItem(STORAGE_KEYS.COUNTRY, `${user.country}_${multi}`, 'cookie');
        }
      }),
      map(({ user }) => {
        if (user.dataProtection) {
          return userActions.setUser({ user });
        }
        return userActions.showDataProtectionDialog({ user });
      })
    )
  );

  changeCountry$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(userActions.changeCountry),
        tap(({ country }) => {
          this._storageService.setItem(STORAGE_KEYS.COUNTRY, `${country}_true`, 'cookie');

          if (country === 'PT') {
            window.location.href = '/home';
          } else {
            location.reload();
          }
        })
      ),
    { dispatch: false }
  );

  chooseEnergyRehabilitation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(userActions.chooseEnergyRehabilitation),
        tap(() =>
          this._dialog.open(EnergyRehabilitationSelectionDialog, {
            disableClose: false,
          })
        )
      ),
    { dispatch: false }
  );

  setEnergyRehabilitation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(userActions.setEnergyRehabilitation),
      tap(({ user, multi }) => {
        if (multi) {
          this._storageService.setItem(STORAGE_KEYS.COUNTRY, `${user.country}_${multi}`, 'cookie');
        }
      }),
      map(({ user }) => {
        if (user.dataProtection) {
          return userActions.setUser({ user });
        }
        return userActions.showDataProtectionDialog({ user });
      })
    )
  );

  persistEnergyRehabilitationMode$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(userActions.setEnergyRehabilitationMode),
        tap(({ enabled }) => this._storageService.setItem(STORAGE_KEYS.ENERGY_REHABILITATION_MODE, String(enabled), 'session'))
      ),
    { dispatch: false }
  );

  showDataProtectionDialog$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(userActions.showDataProtectionDialog),
        tap(({ user }) =>
          this._dialog.open(DataProtectionDialog, {
            disableClose: true,
            maxWidth: '90svw',
            maxHeight: '90svh',
            autoFocus: 'dialog',
            data: user,
          })
        )
      );
    },
    { dispatch: false }
  );

  dataProtectionUpdateUser$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(userActions.showDataProtectionDialog),
      map(({ user }) => userActions.updateUser({ user }))
    );
  });

  dataProtectionUserResponse$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(userActions.userDataProtectionResponse),
      switchMap(({ response, country }) =>
        this._apiUser.putDataProtection(response, country).pipe(
          catchError(() => EMPTY),
          map(() => true),
          tap(() => response && this._floatingAlerts.info('PRIVACY.ACCEPTED_ALERT')),
          filter((value) => value),
          map(() => userActions.apiDataProtectionResponse({ response }))
        )
      )
    );
  });

  dataProtectionApiResponse$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(userActions.apiDataProtectionResponse),
      tap(() => this._dialog.closeAll()),
      map(({ response }) => (response ? userActions.dataProtectionAccepted() : authActions.attemptLogout({})))
    );
  });

  dataProtectionAccepted$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(userActions.dataProtectionAccepted),
      map(() => userActions.fetchUser())
    );
  });

  navigateAfterWebReady$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(userActions.navigateAfterWebReady),
        switchMap(({ route }) =>
          this._store.select(userSelectors.webIsReady).pipe(
            filter((v) => !!v),
            take(1),
            tap(() => this._routing.goToUrl(route))
          )
        )
      ),
    { dispatch: false }
  );

  private _resolveSingleUserFetch(user: AuthUser) {
    // 🇵🇹 Portugal → siempre home
    if (user.country === 'PT') {
      return of(
        userActions.setCountry({ user }),
        userActions.setEnergyRehabilitationMode({ enabled: false }),
        userActions.navigateAfterWebReady({ route: HOME_ROUTE })
      );
    }

    const isComercializadora = user.informationCommercializer;
    const isRehabilitacion = user.informationRehabilitation;

    if (isComercializadora && isRehabilitacion) {
      const storedMode = this._storageService.getItem(STORAGE_KEYS.ENERGY_REHABILITATION_MODE, 'session');

      if (storedMode != null) {
        const isEnabled = storedMode === 'true';
        return of(
          userActions.setCountry({ user }),
          userActions.setEnergyRehabilitationMode({ enabled: isEnabled }),
          userActions.navigateAfterWebReady({ route: isEnabled ? REHAB_ROUTE : HOME_ROUTE })
        );
      }

      return of(userActions.chooseEnergyRehabilitation({ user }));
    }

    if (isRehabilitacion) {
      return of(
        userActions.setCountry({ user }),
        userActions.setEnergyRehabilitationMode({ enabled: true }),
        userActions.navigateAfterWebReady({ route: REHAB_ROUTE })
      );
    }

    return of(
      userActions.setCountry({ user }),
      userActions.setEnergyRehabilitationMode({ enabled: false }),
      userActions.navigateAfterWebReady({ route: HOME_ROUTE })
    );
  }

  constructor(
    private _actions$: Actions,
    private _apiUser: ApiUserService,
    private _store: Store<StoreState>,
    private _routing: AcRoutingService,
    private _floatingAlerts: FloatingAlertsService,
    private _storageService: StorageService,
    private _dialog: MatDialog
  ) {}
}
